import { h } from '../dom';
import type { ScreenCtx, TitleParams } from './context';
import { listNav, type ScreenInstance } from './common';
import { dailyNumber, dateKey } from '../../meta/daily';
import { nextCareerMatch } from '../../meta/career';
import { formatCompact } from '../format';

const FEEDS = ['pitch', 'tight', 'crowd', 'pitch2', 'bench', 'goal', 'gfx', 'pitch', 'crowd2', 'tight2', 'pitch2', 'goal', 'bench', 'gfx', 'pitch', 'tight', 'crowd', 'pitch2'];
const CAM_LABELS = ['MAIN WIDE', 'MAIN TIGHT', 'CROWD', 'HIGH 18 L', 'BENCH', 'BEHIND GOAL', 'GFX', 'HIGH 18 R', 'CROWD', 'HANDHELD', 'REVERSE', 'GOAL R', 'BENCH', 'CG', 'WIDE', 'TIGHT', 'FANS', 'SPIDER'];

function monitorWall(): HTMLElement {
  const wall = h('div', { class: 'sd-tw__wall' });
  FEEDS.forEach((f, i) => {
    const tally = i === 7 ? 'is-pgm' : i === 8 ? 'is-pvw' : '';
    wall.append(h('div', { class: `sd-tw__mon ${tally}`, style: `--d:${(i * 0.37) % 3}s; --r:${(i * 53) % 7}` }, [
      h('div', { class: `sd-tw__feed feed-${f}` }, [h('i', { class: 'sd-tw__ball' })]),
      h('div', { class: 'sd-tw__umd' }, [h('b', { text: String(i + 1) }), h('span', { text: CAM_LABELS[i] ?? '' })]),
    ]));
  });
  return h('div', { class: 'sd-tw' }, [wall]);
}

export function titleScreen(ctx: ScreenCtx, params: TitleParams = {}): ScreenInstance {
  const save = ctx.store.get();
  const today = dateKey(ctx.now());
  const dn = dailyNumber(today);
  const best = save.daily[today];
  const plan = nextCareerMatch(save.career);

  const item = (n: string, label: string, sub: string, onClick: () => void, extra?: Node | null, cls = '', id = '') =>
    h('button', { class: `sd-menu__item ${cls}`, attrs: { type: 'button', 'data-menu': id || undefined }, on: { click: onClick, mouseenter: (e) => (e.currentTarget as HTMLElement).focus({ preventScroll: true }) } }, [
      h('span', { class: 'sd-menu__n', text: n }),
      h('span', { class: 'sd-menu__label', text: label }),
      h('span', { class: 'sd-menu__sub', text: sub }),
      extra ?? null,
      h('span', { class: 'sd-menu__go', text: '▸' }),
    ]);

  const dailySub = best ? `#${dn} · BEST ${best.letter} (${best.rating})` : `#${Math.max(1, dn)} · TODAY`;
  const careerSub = save.career.champion ? 'CHAMPION · REPLAY THE FINAL' : `${plan.tier.name.toUpperCase()} · MATCH ${plan.matchNumber}/${plan.tier.matches}`;
  // THE FIRST BROADCAST: top of the menu (and focused) until it's been played; afterwards it
  // moves below the match modes as "TRAINING ✓".
  const training = save.training;
  const trainingFirst = !training.done;
  const suggestDaily = params.focus === 'daily';
  const trainingItem = trainingFirst
    ? item('', 'START HERE', 'THE FIRST BROADCAST · 4 MIN', () => ctx.startTraining(),
      h('span', { class: 'sd-menu__start', text: training.skipped ? 'TRAINING' : 'NEW HERE?' }), 'is-start', 'training')
    : item('', 'TRAINING', `THE FIRST BROADCAST${training.letter ? ` · ${training.letter}` : ''} · REPLAY`, () => ctx.startTraining(),
      h('span', { class: 'sd-menu__check', text: '✓ DONE' }), '', 'training');
  const dailyExtra = suggestDaily
    ? h('span', { class: 'sd-menu__next', text: 'TRY THIS NEXT' })
    : best ? null : h('span', { class: 'sd-menu__new', text: 'NEW' });
  const entries: HTMLElement[] = [
    ...(trainingFirst ? [trainingItem] : []),
    item('', 'DAILY MATCH', dailySub, () => ctx.show('daily'), dailyExtra, suggestDaily ? 'is-suggested' : '', 'daily'),
    item('', 'CAREER', careerSub, () => ctx.show('career'), null, '', 'career'),
    item('', 'QUICK MATCH', 'PICK A FIXTURE', () => ctx.show('quick'), null, '', 'quick'),
    ...(trainingFirst ? [] : [trainingItem]),
    item('', 'HOW TO PLAY', 'CONTROLS · GRADING', () => ctx.show('howto'), null, '', 'howto'),
    item('', 'SETTINGS', 'AUDIO · VIDEO · CONTROLS', () => ctx.show('settings'), null, '', 'settings'),
  ];
  entries.forEach((e, i) => { e.querySelector('.sd-menu__n')!.textContent = String(i + 1).padStart(2, '0'); });
  const menu = h('nav', { class: 'sd-menu' }, entries);
  // First launch → training; just finished training → Daily; skipped → Daily.
  const focusId = suggestDaily ? 'daily' : params.focus === 'training' || (trainingFirst && !training.skipped) ? 'training' : 'daily';

  const stats = save.stats.matchesPlayed
    ? h('div', { class: 'sd-title__stats' }, [
      h('span', {}, [h('b', { text: String(save.stats.matchesPlayed) }), ' MATCHES']),
      h('span', {}, [h('b', { text: String(Math.round(save.stats.bestRating)) }), ' BEST RATING']),
      h('span', {}, [h('b', { text: formatCompact(save.stats.peakViewers) }), ' PEAK VIEWERS']),
    ])
    : null;

  const el = h('section', { class: 'sd-screen sd-title' }, [
    monitorWall(),
    h('div', { class: 'sd-title__shade' }),
    h('div', { class: 'sd-title__grain' }),
    h('div', { class: 'sd-title__content' }, [
      h('div', { class: 'sd-title__kicker' }, [h('span', { class: 'sd-rec' }), h('span', { text: 'LIVE FROM THE OB TRUCK' })]),
      h('h1', { class: 'sd-logo' }, [
        h('span', { class: 'sd-logo__l1', text: 'SPORTS' }),
        h('span', { class: 'sd-logo__l2' }, [h('span', { text: 'DIRECTOR' }), h('span', { class: 'sd-logo__dot' })]),
      ]),
      h('p', { class: 'sd-title__tag', text: 'Eight cameras. Twenty-two players. Millions watching. Cut it like a pro.' }),
      menu,
      stats,
    ]),
    h('footer', { class: 'sd-title__foot' }, [
      h('span', { text: `v${ctx.deps.version ?? '0.1'}` }),
      h('span', { class: 'sd-title__keys' }, [h('kbd', { text: '↑' }), h('kbd', { text: '↓' }), ' SELECT ', h('kbd', { text: 'ENTER' }), ' GO']),
    ]),
  ]);

  const buttons = () => Array.from(menu.querySelectorAll<HTMLElement>('.sd-menu__item'));
  return {
    el,
    mounted() { (menu.querySelector<HTMLElement>(`[data-menu="${focusId}"]`) ?? buttons()[0])?.focus({ preventScroll: true }); },
    onKey(e) { return listNav(buttons, e); },
  };
}
